import { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { toast } from 'sonner';
import { ChevronLeft, Trash2, Plus, RefreshCw, ShieldCheck, Play, Pause, Eraser, Smartphone, QrCode, Images, Upload, FileText } from 'lucide-react';
import {
  ajouterScoreGreffier,
  lienIndicesGreffier,
  recupererClassement,
  recupererEtatJeu,
  recupererBestOfEnLigne,
  setEtatJeuGreffier,
  supprimerBestOf,
  supprimerScoreGreffier,
  televerserBestOf,
  verifierCodeGreffier,
  viderClassementGreffier,
  DonneesClassement,
  PhotoEnLigne,
} from '../game/api';
import { formatTemps } from '../game/logic';

export default function Greffier() {
  const [code, setCode] = useState<string>(() => sessionStorage.getItem('greffier_code') || '');
  const [autorise, setAutorise] = useState(false);
  const [verifEnCours, setVerifEnCours] = useState(false);
  const [donnees, setDonnees] = useState<DonneesClassement | null>(null);
  const [jeuActif, setJeuActif] = useState<boolean | null>(null);
  const [photosEnLigne, setPhotosEnLigne] = useState<PhotoEnLigne[]>([]);
  const [fichierPhoto, setFichierPhoto] = useState<File | null>(null);
  const [legendePhoto, setLegendePhoto] = useState('');
  const [televersementEnCours, setTeleversementEnCours] = useState(false);

  const [pseudo, setPseudo] = useState('');
  const [minutes, setMinutes] = useState('');
  const [secondes, setSecondes] = useState('');
  const [penalitesMin, setPenalitesMin] = useState('0');
  const [mots, setMots] = useState('3');
  const [jeton, setJeton] = useState('');
  const [envoiEnCours, setEnvoiEnCours] = useState(false);

  const charger = useCallback(async () => {
    const d = await recupererClassement();
    if (d) setDonnees(d);
    const actif = await recupererEtatJeu();
    if (actif !== null) setJeuActif(actif);
    setPhotosEnLigne(await recupererBestOfEnLigne());
  }, []);

  useEffect(() => {
    if (!autorise) return;
    charger();
    const t = window.setInterval(charger, 8000);
    return () => window.clearInterval(t);
  }, [autorise, charger]);

  useEffect(() => {
    const memorise = sessionStorage.getItem('greffier_code');
    if (memorise) {
      verifierCodeGreffier(memorise).then((ok) => {
        if (ok) setAutorise(true);
        else sessionStorage.removeItem('greffier_code');
      });
    }
  }, []);

  const valider = async () => {
    if (!code.trim()) return;
    setVerifEnCours(true);
    const ok = await verifierCodeGreffier(code.trim());
    setVerifEnCours(false);
    if (ok) {
      sessionStorage.setItem('greffier_code', code.trim());
      setAutorise(true);
    } else {
      toast.error('Code refusé. Le greffe reste clos.');
    }
  };

  const ajouter = async () => {
    const min = parseInt(minutes || '0', 10);
    const sec = parseInt(secondes || '0', 10);
    const tempsSec = min * 60 + sec;
    if (!pseudo.trim() || tempsSec <= 0) {
      toast.error('Pseudo et temps requis.');
      return;
    }
    setEnvoiEnCours(true);
    const rep = await ajouterScoreGreffier(sessionStorage.getItem('greffier_code') || '', {
      pseudo: pseudo.trim(),
      tempsSec,
      penalitesSec: Math.max(0, parseInt(penalitesMin || '0', 10)) * 60,
      motsTrouves: Math.min(3, Math.max(0, parseInt(mots || '3', 10))),
      jeton: jeton.trim() || undefined,
    });
    setEnvoiEnCours(false);
    if (rep.ok) {
      if (rep.statut === 'deja_enregistre') {
        toast('Ce jeton est déjà au registre — envoi ignoré (première écriture gagne).');
      } else {
        toast.success(`${pseudo.trim()} inscrit au classement (rang ${rep.rang}).`);
        setPseudo('');
        setMinutes('');
        setSecondes('');
        setPenalitesMin('0');
        setMots('3');
        setJeton('');
      }
      charger();
    } else if (rep.conflit) {
      toast.error(`Pseudo déjà pris. Suggestion : ${rep.suggestion || 'ajoutez un suffixe'}.`);
    } else {
      toast.error("L'envoi a échoué. Vérifiez la connexion.");
    }
  };

  const supprimer = async (jetonScore: string, pseudoScore: string) => {
    if (!window.confirm(`Rayer « ${pseudoScore} » du registre ? Ce geste est définitif.`)) return;
    const ok = await supprimerScoreGreffier(sessionStorage.getItem('greffier_code') || '', jetonScore);
    if (ok) {
      toast.success(`${pseudoScore} a été rayé du registre.`);
      charger();
    } else {
      toast.error('Suppression impossible.');
    }
  };

  const basculerJeu = async () => {
    if (jeuActif === null) return;
    const nouveau = !jeuActif;
    const ok = await setEtatJeuGreffier(sessionStorage.getItem('greffier_code') || '', nouveau);
    if (ok) {
      setJeuActif(nouveau);
      toast.success(nouveau ? 'Le jeu est OUVERT : les joueurs peuvent commencer.' : 'Le jeu est FERMÉ : les curieux patienteront.');
    } else {
      toast.error('Impossible de changer l\'état du jeu.');
    }
  };

  const viderClassement = async () => {
    const total = donnees?.total ?? 0;
    if (!window.confirm(`Vider TOUT le classement (${total} score${total > 1 ? 's' : ''}) ? Idéal après une phase d'essai.`)) return;
    if (!window.confirm('Vraiment sûr ? Cette action est définitive.')) return;
    const n = await viderClassementGreffier(sessionStorage.getItem('greffier_code') || '');
    if (n === null) {
      toast.error('Le classement n\'a pas pu être vidé.');
    } else {
      toast.success(`Classement vidé : ${n} score${n > 1 ? 's' : ''} supprimé${n > 1 ? 's' : ''}.`);
      charger();
    }
  };

  const televerserPhoto = async () => {
    if (!fichierPhoto) return;
    setTeleversementEnCours(true);
    const photo = await televerserBestOf(sessionStorage.getItem('greffier_code') || '', fichierPhoto, legendePhoto);
    setTeleversementEnCours(false);
    if (photo) {
      setPhotosEnLigne((p) => [...p, photo]);
      setFichierPhoto(null);
      setLegendePhoto('');
      toast.success('Photo ajoutée au best of.');
    } else {
      toast.error("Le téléversement a échoué. Vérifiez la connexion et le format (image).");
    }
  };

  const supprimerPhoto = async (photo: PhotoEnLigne) => {
    if (!window.confirm('Retirer cette photo du best of ?')) return;
    const ok = await supprimerBestOf(sessionStorage.getItem('greffier_code') || '', photo.id);
    if (ok) {
      setPhotosEnLigne((p) => p.filter((x) => x.id !== photo.id));
      toast.success('Photo retirée.');
    } else {
      toast.error('Suppression impossible.');
    }
  };

  const effacerPartieLocale = () => {
    if (!window.confirm('Effacer la partie en cours sur CE téléphone (chrono, réponses, carnet) ?')) return;
    localStorage.removeItem('sfg_v1');
    toast.success('Partie locale effacée. Ce téléphone repart de zéro.');
  };

  if (!autorise) {
    return (
      <div className="min-h-screen bg-white" data-testid="greffier-porte">
        <div className="max-w-md mx-auto px-6 py-10">
          <Link to="/" className="min-h-[44px] flex items-center gap-1 text-encre-clair font-medium text-sm">
            <ChevronLeft size={18} /> Retour au jeu
          </Link>
          <h1 className="font-script text-encre text-5xl mt-8">Le greffe</h1>
          <p className="petit-cap mt-2">Accès réservé au greffier</p>
          <label htmlFor="code-greffier" className="petit-cap block mt-10 mb-2">
            Code du greffier
          </label>
          <input
            id="code-greffier"
            data-testid="greffier-code-input"
            type="password"
            value={code}
            onChange={(e) => setCode(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && valider()}
            placeholder="••••••••"
            className="w-full min-h-[52px] px-5 rounded-lg border border-encre bg-white text-lg text-texte placeholder:text-encre-clair/60"
          />
          <button
            data-testid="greffier-code-valider-btn"
            className="btn-principal mt-4"
            disabled={verifEnCours || !code.trim()}
            onClick={valider}
          >
            <ShieldCheck size={18} /> Ouvrir le greffe
          </button>
        </div>
      </div>
    );
  }

  const scores = donnees?.scores || [];

  return (
    <div className="min-h-screen bg-white" data-testid="greffier-page">
      <header className="sticky top-0 bg-white border-b border-brume z-10">
        <div className="max-w-md mx-auto px-5 h-14 flex items-center justify-between">
          <Link to="/" className="min-h-[44px] flex items-center gap-1 text-encre-clair font-medium text-sm">
            <ChevronLeft size={18} /> Jeu
          </Link>
          <span className="petit-cap">Le greffe</span>
          <button
            data-testid="greffier-rafraichir-btn"
            onClick={charger}
            className="min-h-[44px] min-w-[44px] flex items-center justify-center text-encre-clair"
            aria-label="Rafraîchir"
          >
            <RefreshCw size={18} />
          </button>
        </div>
      </header>

      <main className="max-w-md mx-auto px-5 py-6 pb-16">
        <section className="carte px-5 py-5">
          <h2 className="petit-cap">Ouverture du jeu</h2>
          <div className="mt-3 flex items-center justify-between gap-4">
            <p className="text-sm text-texte">
              {jeuActif === null
                ? 'État inconnu…'
                : jeuActif
                ? 'Le jeu est OUVERT : quiconque scanne le QR peut jouer.'
                : 'Le jeu est FERMÉ : les visiteurs voient un écran de patience.'}
            </p>
            <span
              className={`shrink-0 w-3.5 h-3.5 rounded-full ${jeuActif ? 'bg-succes' : 'bg-erreur'}`}
              data-testid="etat-jeu-indicateur"
            />
          </div>
          <button
            data-testid="basculer-jeu-btn"
            className={jeuActif ? 'btn-secondaire mt-4' : 'btn-principal mt-4'}
            disabled={jeuActif === null}
            onClick={basculerJeu}
          >
            {jeuActif ? <Pause size={18} /> : <Play size={18} />}
            {jeuActif ? 'Fermer le jeu (faire patienter)' : 'Ouvrir le jeu officiellement'}
          </button>
          <Link to="/qr" data-testid="planche-qr-lien" className="btn-secondaire mt-3">
            <QrCode size={18} /> Imprimer les QR codes
          </Link>
          <a
            href={lienIndicesGreffier(sessionStorage.getItem('greffier_code') || '')}
            data-testid="indices-docx-lien"
            className="btn-secondaire mt-3"
          >
            <FileText size={18} /> Télécharger les indices (Word)
          </a>
        </section>

        <section className="carte px-5 py-5 mt-5">
          <h2 className="petit-cap">Phases d'essai — remise à zéro</h2>
          <button data-testid="vider-classement-btn" className="btn-secondaire mt-4" onClick={viderClassement}>
            <Eraser size={18} /> Vider tout le classement
          </button>
          <button data-testid="effacer-partie-locale-btn" className="btn-secondaire mt-3" onClick={effacerPartieLocale}>
            <Smartphone size={18} /> Effacer la partie de ce téléphone
          </button>
          <p className="text-xs text-encre-clair mt-3">
            Chaque testeur peut aussi effacer sa propre partie via « Recommencer une partie » sur son écran de fin.
          </p>
        </section>

        <section className="carte px-5 py-5 mt-5">
          <h2 className="petit-cap flex items-center gap-2">
            <Plus size={14} /> Inscrire un temps reçu par WhatsApp
          </h2>
          <div className="mt-4 space-y-3">
            <input
              data-testid="greffier-pseudo-input"
              value={pseudo}
              onChange={(e) => setPseudo(e.target.value)}
              maxLength={40}
              placeholder="Prénom du joueur"
              className="w-full min-h-[48px] px-4 rounded-lg border border-encre bg-white text-texte placeholder:text-encre-clair/60"
            />
            <div className="flex gap-3">
              <div className="flex-1">
                <label className="text-xs text-encre-clair block mb-1">Minutes</label>
                <input
                  data-testid="greffier-minutes-input"
                  inputMode="numeric"
                  value={minutes}
                  onChange={(e) => setMinutes(e.target.value.replace(/\D/g, ''))}
                  placeholder="32"
                  className="w-full min-h-[48px] px-4 rounded-lg border border-encre bg-white text-texte text-center tabular-nums placeholder:text-encre-clair/60"
                />
              </div>
              <div className="flex-1">
                <label className="text-xs text-encre-clair block mb-1">Secondes</label>
                <input
                  data-testid="greffier-secondes-input"
                  inputMode="numeric"
                  value={secondes}
                  onChange={(e) => setSecondes(e.target.value.replace(/\D/g, ''))}
                  placeholder="14"
                  className="w-full min-h-[48px] px-4 rounded-lg border border-encre bg-white text-texte text-center tabular-nums placeholder:text-encre-clair/60"
                />
              </div>
              <div className="flex-1">
                <label className="text-xs text-encre-clair block mb-1">Pénal. (min)</label>
                <input
                  data-testid="greffier-penalites-input"
                  inputMode="numeric"
                  value={penalitesMin}
                  onChange={(e) => setPenalitesMin(e.target.value.replace(/\D/g, ''))}
                  className="w-full min-h-[48px] px-4 rounded-lg border border-encre bg-white text-texte text-center tabular-nums"
                />
              </div>
            </div>
            <div className="flex gap-3">
              <div className="flex-1">
                <label className="text-xs text-encre-clair block mb-1">Mots trouvés (0–3)</label>
                <input
                  data-testid="greffier-mots-input"
                  inputMode="numeric"
                  value={mots}
                  onChange={(e) => setMots(e.target.value.replace(/\D/g, ''))}
                  className="w-full min-h-[48px] px-4 rounded-lg border border-encre bg-white text-texte text-center tabular-nums"
                />
              </div>
              <div className="flex-[2]">
                <label className="text-xs text-encre-clair block mb-1">Jeton WhatsApp (facultatif)</label>
                <input
                  data-testid="greffier-jeton-input"
                  value={jeton}
                  onChange={(e) => setJeton(e.target.value.toUpperCase())}
                  maxLength={16}
                  placeholder="7F3A"
                  className="w-full min-h-[48px] px-4 rounded-lg border border-encre bg-white text-texte text-center tracking-widest placeholder:text-encre-clair/60"
                />
              </div>
            </div>
            <p className="text-xs text-encre-clair">
              Reportez le jeton du message WhatsApp : il empêche les doublons. Sans jeton, un jeton « WAxxxx » est créé.
            </p>
            <button data-testid="greffier-ajouter-btn" className="btn-principal" disabled={envoiEnCours} onClick={ajouter}>
              Inscrire au classement
            </button>
          </div>
        </section>

        <section className="carte px-5 py-5 mt-5">
          <h2 className="petit-cap flex items-center gap-2">
            <Images size={14} /> Best of — vos photos
          </h2>
          <p className="text-xs text-encre-clair mt-2">
            Les photos ajoutées ici apparaissent dans le best of de fin de partie, en plus des portraits.
          </p>
          <div className="mt-4 space-y-3">
            <input
              data-testid="bestof-fichier-input"
              type="file"
              accept="image/*"
              onChange={(e) => setFichierPhoto(e.target.files?.[0] || null)}
              className="w-full text-sm text-texte file:mr-3 file:min-h-[40px] file:px-4 file:rounded-full file:border file:border-encre file:bg-white file:text-encre file:font-medium"
            />
            <input
              data-testid="bestof-legende-input"
              value={legendePhoto}
              onChange={(e) => setLegendePhoto(e.target.value)}
              maxLength={120}
              placeholder="Légende (facultative)"
              className="w-full min-h-[48px] px-4 rounded-lg border border-encre bg-white text-texte placeholder:text-encre-clair/60"
            />
            <button
              data-testid="bestof-televerser-btn"
              className="btn-principal"
              disabled={!fichierPhoto || televersementEnCours}
              onClick={televerserPhoto}
            >
              <Upload size={18} /> {televersementEnCours ? 'Téléversement…' : 'Ajouter au best of'}
            </button>
          </div>
          {photosEnLigne.length > 0 && (
            <div className="grid grid-cols-3 gap-3 mt-4" data-testid="bestof-greffier-liste">
              {photosEnLigne.map((p) => (
                <div key={p.id} className="relative border border-brume overflow-hidden">
                  <img src={p.url} alt={p.legende} loading="lazy" className="w-full aspect-square object-cover" />
                  <button
                    data-testid={`bestof-supprimer-${p.id}`}
                    onClick={() => supprimerPhoto(p)}
                    className="absolute top-1 right-1 w-8 h-8 rounded-full bg-white border border-erreur text-erreur flex items-center justify-center"
                    aria-label="Retirer la photo"
                  >
                    <Trash2 size={14} />
                  </button>
                </div>
              ))}
            </div>
          )}
        </section>

        <section className="mt-8">
          <h2 className="petit-cap">Le registre — {donnees?.total ?? 0} inscrit{(donnees?.total ?? 0) > 1 ? 's' : ''}</h2>
          {scores.length === 0 ? (
            <p className="text-encre-clair text-sm mt-3">Aucun score au registre.</p>
          ) : (
            <ol className="mt-3 space-y-2" data-testid="greffier-liste">
              {scores.map((s) => (
                <li key={s.jeton || s.rang} className="flex items-center gap-3 px-4 py-3 rounded-lg border border-brume bg-white">
                  <span className="w-8 h-8 shrink-0 rounded-full border border-encre text-encre text-sm font-semibold flex items-center justify-center tabular-nums">
                    {s.rang}
                  </span>
                  <div className="flex-1 min-w-0">
                    <p className="text-encre font-medium truncate">{s.pseudo}</p>
                    <p className="text-xs text-encre-clair">
                      jeton {s.jeton || '—'}
                      {s.penalitesSec > 0 ? ` · +${Math.round(s.penalitesSec / 60)} min` : ''} · {s.motsTrouves}/3 mots
                    </p>
                  </div>
                  <span className="text-encre font-semibold tabular-nums">{formatTemps(s.tempsSec)}</span>
                  <button
                    data-testid={`greffier-supprimer-${s.jeton}`}
                    onClick={() => s.jeton && supprimer(s.jeton, s.pseudo)}
                    className="min-h-[44px] min-w-[44px] flex items-center justify-center text-erreur"
                    aria-label={`Supprimer ${s.pseudo}`}
                  >
                    <Trash2 size={18} />
                  </button>
                </li>
              ))}
            </ol>
          )}
        </section>
      </main>
    </div>
  );
}
